<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import UpsertBillEstimateDialog from '@/components/eos/forms/UpsertBillEstimateDialog.vue'
import GenerateBillEstimatePDFDialog from '@/components/eos/forms/GenerateBillEstimatePDFDialog.vue'
import { NotifyFail } from '@/helpers/core/notifications'
import { getAllSites } from '@/services/core.service'
import {
  deleteEnergyBillEstimation,
  generateEnergyBillEstimation,
  getEnergyBillEstimations,
} from '@/services/eos/billing-estimates.service'
import type { ILiteSiteFilters } from '@/types/core/filters'
import { SiteType, type LiteSite } from '@/types/core/Site'
import type { IBillEstimation, IBillEstimationDashboardRow } from '@/types/eos/BillEstimation'
import type { IEnergyBillEstimationFilters, IEnergyBillEstimationGenerationFilters } from '@/types/eos/filters'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { formatCurrency } from '@/util/formatters'
import { downloadLinkTarget } from '@/util'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { ElMessage } from 'element-plus'
import { computed, onMounted, reactive, ref } from 'vue'

const createDialogVisible = ref<boolean>(false)
const editDialogVisible = ref<boolean>(false)
const editRowData = ref<IBillEstimationDashboardRow | undefined>(undefined)
const generatePDFDialogVisible = ref<boolean>(false)
const siteFilterOptions = ref<LiteSite[]>([])
const currentMonthBillEstimation = ref<IBillEstimation>()
const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const filters = reactive<IEnergyBillEstimationFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[0],
  order_by: undefined,
  order_by_order: OrderBy.DESC,
  site: undefined,
  month: undefined,
  year: undefined,
})

// Vue Query
const queryClient = useQueryClient()

const { isFetching, isPending, isError, isRefetching, data, error, refetch } = useQuery({
  queryKey: ['bill-estimations', filters],
  queryFn: () => getEnergyBillEstimations(filters),
  placeholderData: (prev: any) => prev,
})

// Compute message to display when table is empty.
const tableEmptyText = computed(() => {
  if (isFetching.value) {
    return 'Loading...'
  } else {
    return 'No data'
  }
})

const currentMonthEstimationTooltipText = computed(() => {
  if (!currentMonthBillEstimation.value) {
    return ''
  }

  const tooltipValue = currentMonthBillEstimation.value.fees
    .map((fee) => `${fee.name}: ${formatCurrency(parseFloat(fee.amount))}`)
    .join('<br>')

  return `<b>${monthNames[currentMonthBillEstimation.value.month - 1]} ${currentMonthBillEstimation.value.year}</b><br>${tooltipValue}`
})

// DELETE mutation
const handleDelete = useMutation({
  mutationFn: (id: number) => {
    return deleteEnergyBillEstimation(id)
  },
  onSuccess: () => {
    ElMessage({
      message: 'Entry deleted successfully.',
      type: 'success',
    })
    queryClient.invalidateQueries({ queryKey: ['bill-estimations'] })
  },
  onError: (e) => {
    ElMessage({
      message: e.message,
      type: 'error',
    })
  },
})

const handleSiteFilterChange = async () => {
  if (!filters.site) return

  try {
    const currentDate = new Date()

    const estimationFilters: IEnergyBillEstimationGenerationFilters = {
      period: currentDate,
      site: filters.site,
      response_format: 'json',
    }

    currentMonthBillEstimation.value = (await generateEnergyBillEstimation(estimationFilters)) as IBillEstimation
  } catch {
    NotifyFail('Failed to generate a bill estimation for the current month', {
      title: 'Bill estimation generation failed',
    })
  }
}

const currencyFormatter = (row: any, column: any, cellValue: number) => {
  return formatCurrency(cellValue)
}

onMounted(async () => {
  try {
    const filters: ILiteSiteFilters = { type: SiteType.DataCenter }
    const sitesData = await getAllSites(filters)

    if (sitesData) {
      siteFilterOptions.value = sitesData
    }
  } catch {
    NotifyFail('Failed to fetch resources')
  }
})
</script>

<template>
  <ViewLayout>
    <template v-if="!isError" #controls>
      <el-button type="primary" :disabled="isFetching" @click="generatePDFDialogVisible = true">Generate PDF</el-button>
      <el-button type="primary" :disabled="isFetching" @click="createDialogVisible = true">Add actual</el-button>
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="inline-flex items-center gap-spacing-sm">
        <el-select
          v-model="filters.site"
          style="width: 232px"
          placeholder="Site"
          :disabled="isPending"
          clearable
          @change="handleSiteFilterChange"
          @clear="currentMonthBillEstimation = undefined"
        >
          <el-option v-for="s in siteFilterOptions" :key="s.site_name" :label="s.site_name" :value="s.site_id" />
        </el-select>
        <el-descriptions size="small" border>
          <el-descriptions-item>
            <template #label>
              <div>Current month bill estimation</div>
            </template>
            <template v-if="!currentMonthBillEstimation && !filters.site"> Choose a site </template>
            <template v-else-if="!currentMonthBillEstimation && filters.site">
              <Icon icon="eos-icons:three-dots-loading" />
            </template>
            <template v-else>
              {{ currentMonthBillEstimation?.estimated_value }}
            </template>
          </el-descriptions-item>
        </el-descriptions>
        <el-tooltip
          v-if="currentMonthBillEstimation"
          effect="dark"
          :content="currentMonthEstimationTooltipText"
          :raw-content="true"
          placement="right"
        >
          <Icon icon="mdi:information-outline" />
        </el-tooltip>
      </div>
      <el-table v-loading="isFetching" :data="data?.data" :empty-text="tableEmptyText" stripe>
        <el-table-column prop="site.name" label="Site" :min-width="40" />
        <el-table-column label="Period" :min-width="40">
          <template #default="scope">
            {{ `${monthNames[scope.row.month - 1]} ${scope.row.year}` }}
          </template>
        </el-table-column>
        <el-table-column label="Projected Energy Costs" :min-width="100">
          <template #default="scope">
            <el-table :data="scope.row.estimated_value_fees" empty-text="No fees" :show-header="false">
              <el-table-column prop="name" label="Name" :min-width="100" />
              <el-table-column prop="amount" label="Amount" :formatter="currencyFormatter" :min-width="60" />
            </el-table>
            <b class="flex justify-center items-center">{{ `Total: ${formatCurrency(scope.row.estimated_value)}` }}</b>
          </template>
        </el-table-column>
        <el-table-column label="Actual Energy Costs" :min-width="100">
          <template #default="scope">
            <el-table :data="scope.row.actual_value_fees" empty-text="No fees" :show-header="false">
              <el-table-column prop="name" label="Name" :min-width="100" />
              <el-table-column prop="amount" label="Amount" :formatter="currencyFormatter" :min-width="60" />
            </el-table>
            <b class="flex justify-center items-center">{{ `Total: ${formatCurrency(scope.row.actual_value)}` }}</b>
          </template>
        </el-table-column>
        <el-table-column label="Attachments" :min-width="100" align="left">
          <template #default="scope">
            <div
              v-if="scope.row.attachments && scope.row.attachments.length > 0"
              class="flex flex-col gap-spacing-xs items-start"
            >
              <a
                v-for="attachment in scope.row.attachments"
                :key="attachment.id"
                class="block text-text-brand no-underline cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap hover:underline"
                :title="`Download ${attachment.name}`"
                @click.prevent="downloadLinkTarget(attachment.file_url, attachment.name)"
              >
                {{ attachment.name }}
              </a>
            </div>
            <span v-else class="text-text-tertiary">No attachments</span>
          </template>
        </el-table-column>
        <el-table-column :min-width="40" align="center">
          <template #default="scope">
            <el-button
              link
              title="Edit"
              @click="
                () => {
                  editRowData = scope.row as IBillEstimationDashboardRow
                  editDialogVisible = true
                }
              "
            >
              <Icon icon="material-symbols:edit-outline" :height="20" />
            </el-button>
            <el-button
              link
              title="Delete"
              @click="
                DeleteConfirmationModal(
                  'Delete Bill Estimate',
                  'Are you sure you want to delete this estimate? This action cannot be undone',
                  scope.row.id,
                  handleDelete.mutateAsync
                )
              "
            >
              <Icon icon="material-symbols:delete-outline" :height="20" />
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="data && data.total_pages > 0"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="self-end"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isFetching"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
      <UpsertBillEstimateDialog v-model="createDialogVisible" :sites="siteFilterOptions" :default-site="filters.site" />
      <UpsertBillEstimateDialog
        v-model="editDialogVisible"
        :sites="siteFilterOptions"
        :edit-data="editRowData"
        @update:model-value="
          (val: boolean | undefined) => {
            editDialogVisible = val ?? false
            if (!val) editRowData = undefined
          }
        "
      />
      <GenerateBillEstimatePDFDialog
        v-model="generatePDFDialogVisible"
        :sites="siteFilterOptions"
        :default-site="filters.site"
      />
    </template>
  </ViewLayout>
</template>
